import { operatorSession, useOperatorSession } from '@/auth/runtime';
import type { OperatorSession } from '@/auth/session';
import { useIsWide } from '@/lib/media-query';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@mgrvtechgroup/admin-ds';
import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router';
import { DEV_NAV_SECTIONS, isNavItemActive, NAV_SECTIONS } from './nav';
import { UserMenu } from './user-menu';

/**
 * 좌측 내비게이션 — 맨 위 앱 이름과 메뉴만 둔다.
 * - 768px 미만: admin-ds가 시트로 바꾼다. 메뉴를 고르면 시트를 닫는다.
 * - 768~1023px(태블릿 세로): 아이콘만 남기고 접어 본문 폭을 확보한다.
 */
export function AppSidebar({
  session: activeSession = operatorSession,
}: {
  session?: OperatorSession;
}) {
  const { pathname } = useLocation();
  const session = useOperatorSession(activeSession);
  const { setOpen, setOpenMobile, isMobile } = useSidebar();
  const isWide = useIsWide();
  // 패턴 견본(더미 페이지)은 개발 서버에서 항상 메뉴에 둔다. production 빌드에는 라우트도 메뉴도 없다.
  const sections = import.meta.env.DEV ? [...NAV_SECTIONS, ...DEV_NAV_SECTIONS] : NAV_SECTIONS;

  // 폭 구간이 실제로 바뀔 때만 맞춘다 — 같은 구간 안에서 사용자가 펼치고 접은 상태는 그대로 둔다.
  // admin-ds의 setOpen은 열림 상태가 바뀔 때마다 새 함수라, 의존성만으로 판단하면 토글 직후 되돌려 버린다.
  const appliedWide = useRef<boolean | null>(null);
  useEffect(() => {
    if (appliedWide.current === isWide) {
      return;
    }
    appliedWide.current = isWide;
    setOpen(isWide);
  }, [isWide, setOpen]);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1.5">
          <span className="bg-foreground text-background caption flex size-8 shrink-0 items-center justify-center rounded-md font-semibold">
            M
          </span>
          <span className="label truncate group-data-[collapsible=icon]:hidden">
            MGRV Core Admin
          </span>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {sections.map((section, index) => (
          <SidebarGroup key={section.label ?? index}>
            {section.label && <SidebarGroupLabel>{section.label}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={isNavItemActive(pathname, item)}
                        tooltip={isMobile ? undefined : item.label}
                        render={<NavLink to={item.path} end={item.path === '/dev'} />}
                        onClick={() => {
                          setOpenMobile(false);
                        }}
                      >
                        <Icon />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <div className="flex items-center justify-between gap-2 px-1 group-data-[collapsible=icon]:flex-col-reverse">
          {['authorized', 'forbidden', 'service-failure', 'checking'].includes(session.status) ? (
            <SidebarMenu className="min-w-0 flex-1 group-data-[collapsible=icon]:flex-none">
              <SidebarMenuItem>
                <UserMenu session={activeSession} />
              </SidebarMenuItem>
            </SidebarMenu>
          ) : (
            <span className="caption text-muted-foreground truncate group-data-[collapsible=icon]:hidden">
              {session.status === 'not-configured'
                ? '운영자 인증 미연동'
                : session.status === 'unauthenticated'
                  ? '로그인 필요'
                  : '업무 권한 확인'}
            </span>
          )}
          <SidebarTrigger aria-label="사이드바 접기/펼치기" title="사이드바 접기/펼치기" />
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
